import { keepPreviousData } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ChevronRight, Plus } from "lucide-react";
import type { ReactNode } from "react";
import {
  Badge,
  Button,
  buttonVariants,
  Card,
  Combobox,
  Pagination,
  Skeleton,
  TBody,
  Table,
  Td,
  Th,
  THead,
  Tr,
} from "@rumbledirect/ui-v2";
import {
  CONFIGURATOR_REQUEST_STATUSES,
  CONFIGURATOR_TYPES,
  type ConfiguratorRequest,
  type ConfiguratorRequestStatus,
  type ConfiguratorType,
} from "@/configurator/requests/configuratorRequest";
import {
  configuratorTypeLabel,
  requestEmployeeLabel,
  requestStatusLabel,
  requestStatusTone,
  requestVehicleLabel,
} from "@/configurator/requests/configuratorRequestFields";
import { useGetAdminConfiguratorRequests } from "@/lib/api/generated/configurator/configurator";
import {
  formatCentsAsEuros,
  formatDateOrEmpty,
} from "@rumbledirect/ui-v2/format";
import { RequirePermission } from "@/currentUser/RequirePermission";
import { TopbarActions } from "@/shell/TopbarActions";

const CREATE_PERMISSION = "admin_configurator_request_create";

const ITEMS_PER_PAGE = 20;
const SKELETON_ROW_KEYS = Array.from(
  { length: 5 },
  (_, index) => `configurator-request-skeleton-${index}`,
);

/** Statuses the overview can be narrowed to. Filtering happens server-side. */
const STATUS_OPTIONS = CONFIGURATOR_REQUEST_STATUSES.map((value) => ({
  value,
  label: requestStatusLabel(value),
}));

/** Where a request was put together; filtered server-side as well. */
const CONFIGURATOR_TYPE_OPTIONS = CONFIGURATOR_TYPES.map((value) => ({
  value,
  label: configuratorTypeLabel(value),
}));

interface RequestsSearch {
  readonly page: number;
  readonly status?: ConfiguratorRequestStatus;
  readonly configuratorType?: ConfiguratorType;
}

function isRequestStatus(value: unknown): value is ConfiguratorRequestStatus {
  return CONFIGURATOR_REQUEST_STATUSES.includes(
    value as ConfiguratorRequestStatus,
  );
}

function isConfiguratorType(value: unknown): value is ConfiguratorType {
  return CONFIGURATOR_TYPES.includes(value as ConfiguratorType);
}

export const Route = createFileRoute("/_authenticated/configurator/requests")({
  validateSearch: (search: Record<string, unknown>): RequestsSearch => {
    const page = Number(search.page);

    return {
      page: Number.isInteger(page) && page >= 1 ? page : 1,
      status: isRequestStatus(search.status) ? search.status : undefined,
      configuratorType: isConfiguratorType(search.configuratorType)
        ? search.configuratorType
        : undefined,
    };
  },
  component: ConfiguratorRequestsPage,
});

function ConfiguratorRequestsPage(): ReactNode {
  const { page, status, configuratorType } = Route.useSearch();
  const navigate = Route.useNavigate();

  const { data, isPending, isPlaceholderData, isError, refetch } =
    useGetAdminConfiguratorRequests(
      {
        page,
        itemsPerPage: ITEMS_PER_PAGE,
        ...(status ? { status } : {}),
        ...(configuratorType ? { configuratorType } : {}),
      },
      { query: { placeholderData: keepPreviousData } },
    );

  const totalItems = data?.totalItems ?? 0;
  const members = data?.member;

  return (
    <div className="flex flex-col gap-5">
      <TopbarActions>
        <RequirePermission permission={CREATE_PERMISSION}>
          <Link
            to="/configurator/requests/new"
            className={buttonVariants({ size: "sm" })}
          >
            <Plus className="size-4" aria-hidden="true" />
            Nieuwe aanvraag
          </Link>
        </RequirePermission>
      </TopbarActions>

      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Aanvragen</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Wat medewerkers hebben samengesteld en ingediend op hun uitnodiging.
        </p>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="w-full max-w-xs">
          <Combobox
            aria-label="Filter op status"
            placeholder="Alle statussen"
            value={status}
            onValueChange={(next) =>
              // Back to page 1: the current page number is meaningless against
              // a different result set.
              void navigate({
                search: (prev) => ({
                  ...prev,
                  page: 1,
                  status: isRequestStatus(next) ? next : undefined,
                }),
              })
            }
            options={STATUS_OPTIONS}
            searchable={false}
            clearable
            fluid
          />
        </div>

        <div className="w-full max-w-xs">
          <Combobox
            aria-label="Filter op soort aanvraag"
            placeholder="Intern en extern"
            value={configuratorType}
            onValueChange={(next) =>
              void navigate({
                search: (prev) => ({
                  ...prev,
                  page: 1,
                  configuratorType: isConfiguratorType(next) ? next : undefined,
                }),
              })
            }
            options={CONFIGURATOR_TYPE_OPTIONS}
            searchable={false}
            clearable
            fluid
          />
        </div>
      </div>

      <Card className="overflow-hidden">
        <Table>
          <THead>
            <Tr>
              <Th>Medewerker</Th>
              <Th className="w-24">Soort</Th>
              <Th>Voertuig</Th>
              <Th className="w-40">Inrichting</Th>
              <Th className="w-36">Ingediend op</Th>
              <Th className="w-32 text-right">Totaal</Th>
              <Th className="w-32">Status</Th>
              <Th className="w-16 text-right">
                <span className="sr-only">Acties</span>
              </Th>
            </Tr>
          </THead>
          <TBody
            aria-busy={isPlaceholderData}
            className={isPlaceholderData ? "opacity-60" : undefined}
          >
            <ConfiguratorRequestRows
              isPending={isPending}
              isError={isError}
              members={members}
              isFiltered={
                status !== undefined || configuratorType !== undefined
              }
              onRetry={() => void refetch()}
            />
          </TBody>
        </Table>
      </Card>

      {totalItems > ITEMS_PER_PAGE && (
        <Pagination
          page={page}
          pageSize={ITEMS_PER_PAGE}
          totalItems={totalItems}
          status={isPending ? "pending" : isError ? "error" : "ready"}
          onPageChange={(next) =>
            void navigate({ search: (prev) => ({ ...prev, page: next }) })
          }
        />
      )}
    </div>
  );
}

function ConfiguratorRequestRows({
  isPending,
  isError,
  members,
  isFiltered,
  onRetry,
}: {
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly members: readonly ConfiguratorRequest[] | undefined;
  readonly isFiltered: boolean;
  readonly onRetry: () => void;
}): ReactNode {
  if (isPending) {
    return SKELETON_ROW_KEYS.map((key) => (
      <Tr key={key}>
        <Td>
          <Skeleton className="h-4 w-40" />
        </Td>
        <Td>
          <Skeleton className="h-5 w-16" />
        </Td>
        <Td>
          <Skeleton className="h-4 w-36" />
        </Td>
        <Td>
          <Skeleton className="h-4 w-28" />
        </Td>
        <Td>
          <Skeleton className="h-4 w-24" />
        </Td>
        <Td>
          <div className="flex justify-end">
            <Skeleton className="h-4 w-20" />
          </div>
        </Td>
        <Td>
          <Skeleton className="h-5 w-20" />
        </Td>
        <Td>
          <div className="flex justify-end">
            <Skeleton className="h-8 w-8" />
          </div>
        </Td>
      </Tr>
    ));
  }

  if (isError) {
    return (
      <Tr>
        <Td colSpan={8} className="py-10 text-center">
          <div className="space-y-3" role="alert">
            <p className="text-muted-foreground">
              We konden de aanvragen niet laden.
            </p>
            <Button variant="outline" size="sm" onClick={onRetry}>
              Opnieuw proberen
            </Button>
          </div>
        </Td>
      </Tr>
    );
  }

  if (members === undefined || members.length === 0) {
    return (
      <Tr>
        <Td colSpan={8} className="py-10 text-center text-muted-foreground">
          {isFiltered
            ? "Geen aanvragen met deze filters."
            : "Nog geen aanvragen."}
        </Td>
      </Tr>
    );
  }

  return members.map((request) => (
    <Tr key={request.id}>
      <Td className="font-medium">
        <RequestDetailLink request={request}>
          {requestEmployeeLabel(request)}
        </RequestDetailLink>
      </Td>
      <Td>
        <Badge
          variant={request.configuratorType === "external" ? "info" : "neutral"}
          size="sm"
        >
          {configuratorTypeLabel(request.configuratorType)}
        </Badge>
      </Td>
      <Td>{requestVehicleLabel(request)}</Td>
      <Td className="text-muted-foreground">
        {request.vehicleConversionTitle}
      </Td>
      <Td>{formatDateOrEmpty(request.submittedAt)}</Td>
      <Td className="text-right tabular-nums">
        {/*
          A request put together elsewhere has no lines to sum, so its total is
          zero by definition. Printing that would read as a free car; the column
          is left empty instead, the way the price is simply not known here.
        */}
        {request.configuratorType === "external"
          ? null
          : formatCentsAsEuros(request.totalPrice)}
      </Td>
      <Td>
        <Badge variant={requestStatusTone(request.status)} size="sm">
          {requestStatusLabel(request.status)}
        </Badge>
      </Td>
      <Td>
        <div className="flex justify-end">
          <RequestDetailLink
            request={request}
            aria-label={`Aanvraag van ${requestEmployeeLabel(request)} openen`}
            className={buttonVariants({ variant: "ghost", size: "icon-sm" })}
          >
            <ChevronRight className="size-4" aria-hidden="true" />
          </RequestDetailLink>
        </div>
      </Td>
    </Tr>
  ));
}

/**
 * Links a row to the request's detail page, where the full configuration and
 * the accord/decline actions live. Renders plain text for a request without an
 * id, which the API should not return but the generated type allows.
 */
function RequestDetailLink({
  request,
  children,
  className,
  ...rest
}: {
  readonly request: ConfiguratorRequest;
  readonly children: ReactNode;
  readonly className?: string;
  readonly "aria-label"?: string;
}): ReactNode {
  if (!request.id) return children;

  return (
    <Link
      {...rest}
      to="/configurator/requests/$configuratorRequestId"
      params={{ configuratorRequestId: request.id }}
      search={{ tab: "algemeen" }}
      className={
        className ??
        "rounded-sm transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      }
    >
      {children}
    </Link>
  );
}
